import {
  Box,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GradientRow {
  Time: string;
  Conditions: string;
  /** Dynamic pump columns, e.g. PumpA, PumpB, PumpC … */
  [pumpKey: string]: string;
}

export interface GradientProgramValue {
  totalPumps: number;
  gradientRows: GradientRow[];
}

interface GradientProgramFieldProps {
  label: string;
  value: GradientProgramValue | null;
  /** Default number of pumps from the catalog definition (pumpCount). */
  defaultPumpCount?: number;
  onChange: (v: GradientProgramValue) => void;
  required?: boolean;
  placeholder?: string;
}

const MIN_PUMPS = 1;
const MAX_PUMPS = 26;

function clampPumpCount(value: number): number {
  if (!Number.isFinite(value)) return MIN_PUMPS;
  return Math.min(MAX_PUMPS, Math.max(MIN_PUMPS, Math.floor(value)));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pumpKey(index: number): string {
  return `Pump${String.fromCharCode(65 + index)}`; // PumpA, PumpB, …
}

function makeEmptyRow(totalPumps: number): GradientRow {
  const row: GradientRow = { Time: '', Conditions: '' };
  for (let i = 0; i < totalPumps; i++) {
    row[pumpKey(i)] = '';
  }
  return row;
}

function migrateRows(rows: GradientRow[], newTotal: number): GradientRow[] {
  return rows.map((row) => {
    const newRow: GradientRow = { Time: row.Time, Conditions: row.Conditions };
    for (let i = 0; i < newTotal; i++) {
      newRow[pumpKey(i)] = row[pumpKey(i)] ?? '';
    }
    return newRow;
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reusable gradient-program parameter field.
 * Mirrors Angular's `app-gradient-program-parameter` component.
 *
 * Renders a dynamic table with configurable pump columns (PumpA … PumpN),
 * a Time column, a Conditions column, and add/remove row controls.
 */
export function GradientProgramField({
  label,
  value,
  defaultPumpCount = 2,
  onChange,
  required = false,
  placeholder,
}: GradientProgramFieldProps) {
  const resolvedPumps = clampPumpCount(value?.totalPumps ?? defaultPumpCount);
  const current: GradientProgramValue = {
    totalPumps: resolvedPumps,
    gradientRows: value?.gradientRows ?? [makeEmptyRow(resolvedPumps)],
  };

  const pumps = Array.from({ length: current.totalPumps }, (_, i) => pumpKey(i));

  // ---- mutation helpers ---------------------------------------------------

  const updateRow = (rowIdx: number, key: string, val: string) => {
    const rows = current.gradientRows.map((row, i) =>
      i === rowIdx ? { ...row, [key]: val } : row,
    );
    onChange({ ...current, gradientRows: rows });
  };

  const addRow = () => {
    onChange({
      ...current,
      gradientRows: [...current.gradientRows, makeEmptyRow(current.totalPumps)],
    });
  };

  const removeRow = (rowIdx: number) => {
    onChange({
      ...current,
      gradientRows: current.gradientRows.filter((_, i) => i !== rowIdx),
    });
  };

  const changePumpCount = (n: number) => {
    const nextCount = clampPumpCount(n);
    onChange({
      totalPumps: nextCount,
      gradientRows: migrateRows(current.gradientRows, nextCount),
    });
  };

  // ---- render -------------------------------------------------------------

  const cellSx = {
    p: 0.25,
    borderBottom: '1px solid',
    borderColor: 'divider',
  } as const;

  const headerSx = {
    ...cellSx,
    fontWeight: 600,
    fontSize: 12,
    color: 'text.secondary',
    minWidth: 80,
    whiteSpace: 'nowrap',
    userSelect: 'none',
  } as const;

  return (
    <Box>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
        <TextField
          select
          size="small"
          label="Pumps"
          value={current.totalPumps}
          onChange={(e) => changePumpCount(Number(e.target.value))}
          sx={{ width: 100 }}
        >
          {Array.from({ length: MAX_PUMPS }, (_, i) => i + MIN_PUMPS).map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Box
          component="table"
          sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={headerSx}>
                Time
              </Box>
              {pumps.map((p) => (
                <Box key={p} component="th" sx={headerSx}>
                  {p}
                </Box>
              ))}
              <Box component="th" sx={headerSx}>
                Conditions
              </Box>
              <Box component="th" sx={{ ...headerSx, width: 36, textAlign: 'center' }}>
                Actions
              </Box>
            </Box>
          </Box>

          <Box component="tbody">
            {current.gradientRows.map((row, rowIdx) => (
              <Box component="tr" key={rowIdx}>
                {/* Time */}
                <Box component="td" sx={cellSx}>
                  <TextField
                    size="small"
                    value={row.Time}
                    onChange={(e) => updateRow(rowIdx, 'Time', e.target.value)}
                    slotProps={{ htmlInput: { style: { padding: '4px 6px', fontSize: 12 } } }}
                    sx={{ width: 80 }}
                  />
                </Box>

                {/* Pump columns */}
                {pumps.map((p) => (
                  <Box key={p} component="td" sx={cellSx}>
                    <TextField
                      size="small"
                      value={row[p] ?? ''}
                      onChange={(e) => updateRow(rowIdx, p, e.target.value)}
                      slotProps={{ htmlInput: { style: { padding: '4px 6px', fontSize: 12 } } }}
                      sx={{ width: 80 }}
                    />
                  </Box>
                ))}

                {/* Conditions */}
                <Box component="td" sx={cellSx}>
                  <TextField
                    size="small"
                    value={row.Conditions}
                    onChange={(e) => updateRow(rowIdx, 'Conditions', e.target.value)}
                    slotProps={{ htmlInput: { style: { padding: '4px 6px', fontSize: 12 } } }}
                    sx={{ width: 80 }}
                  />
                </Box>

                {/* Remove row */}
                <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeRow(rowIdx)}
                    disabled={current.gradientRows.length <= 1}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        sx={{ mt: 1 }}
        variant="outlined"
      >
        Add Row
      </Button>
    </Box>
  );
}
